"use client";

import { useState } from "react";
import { Play, Quote, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Voices() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const testimonials = [{
    name: "Dr. Sarah Jenkins",
    role: "Wildlife Biologist",
    company: "Refuge Research Group",
    quote: "The advocacy work done here is vital for the survival of our nesting sea birds on Protection Island.",
    thumbnail: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722645/img/biologist_field_study.png",
    thumbnailSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722645/img/biologist_field_study.png-wvc-srcset",
    thumbnailSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722645/img/biologist_field_study.png-wvc-sizes",
    headshot: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722645/img/headshot_sarah_jenkins.webp",
    headshotSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722645/img/headshot_sarah_jenkins.webp-wvc-srcset",
    headshotSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722645/img/headshot_sarah_jenkins.webp-wvc-sizes",
    span: "lg:col-span-6"
  }, {
    name: "Mark Thompson",
    role: "Local Resident",
    company: "Dungeness Community",
    quote: "Friends of the Refuges helped me understand how to protect the fragile ecosystem right in my backyard.",
    thumbnail: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722645/img/resident_beach_walk.png",
    thumbnailSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722645/img/resident_beach_walk.png-wvc-srcset",
    thumbnailSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722645/img/resident_beach_walk.png-wvc-sizes",
    headshot: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722645/img/headshot_mark_thompson.png",
    headshotSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722645/img/headshot_mark_thompson.png-wvc-srcset",
    headshotSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722645/img/headshot_mark_thompson.png-wvc-sizes",
    span: "lg:col-span-6"
  }];
  const diamondPlateSvg = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='28' height='14' viewBox='0 0 28 14'><path d='M0 7 L7 0 L14 7 L21 0 L28 7 L21 14 L14 7 L7 14 Z' fill='none' stroke='%23228B22' stroke-width='1'/></svg>";
  return <section data-section-id="1746" id="voices-for-the-wild" className="bg-background text-foreground">
      <div aria-hidden="true" className="h-[14px] w-full" style={{
      backgroundImage: `url("${diamondPlateSvg}")`,
      backgroundRepeat: "repeat"
    }} />

      <div className="px-6 md:px-10 lg:px-16 py-24 md:py-36 max-w-[1440px] mx-auto">
        <div ref={headerRef} className={`grid grid-cols-12 gap-6 md:gap-8 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="col-span-12 lg:col-span-8">
            <p className="font-mono text-[0.6875rem] md:text-xs uppercase tracking-[0.18em] text-blue-600 flex items-center gap-3" style={{
            letterSpacing: "0.18em"
          }}>
              <span>REFUGE · ADVOCACY</span>
              <span aria-hidden="true" className="inline-block w-2 h-2 bg-green-600" />
              <span>VOICES FOR THE WILD</span>
            </p>
            <h2 className="font-default uppercase font-semibold mt-6 text-foreground" style={{
            fontSize: "clamp(2.25rem, 4.8vw, 4.75rem)",
            lineHeight: "0.92",
            letterSpacing: "-0.03em"
          }}>
              WHAT OUR COMMUNITY{" "}
              <span className="relative inline-block">
                <span className="relative z-10">TRULY</span>
                <span aria-hidden="true" className="absolute left-[-4%] right-[-4%] top-1/2 h-[14px] md:h-[18px] bg-green-100 z-0" style={{
                transform: "translateY(-50%) rotate(-2deg)"
              }} />
              </span>{" "}
              VALUES
            </h2>
          </div>

          <div className="col-span-12 lg:col-span-4 flex lg:items-end">
            <div className="w-full lg:text-right">
              <div className="flex lg:justify-end items-center gap-2 font-mono text-xs uppercase tracking-[0.04em] text-foreground/70">
                <span aria-hidden="true" className="inline-block w-2.5 h-2.5 bg-blue-600 animate-pulse" />
                <span>AUTHENTIC PERSPECTIVES</span>
              </div>
              <p className="font-mono text-xs uppercase tracking-[0.12em] text-foreground/60 mt-3 lg:text-right">
                2 ADVOCATES ON RECORD
              </p>
            </div>
          </div>
        </div>

        <div className="mt-10 md:mt-14 flex items-center">
          <div className="flex-1 h-px bg-foreground/20" />
          <div className="w-1.5 h-1.5 bg-green-600" />
        </div>

        <div ref={gridRef} className={`mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {testimonials.map((t, i) => <article data-index={i} key={t.name} className={`col-span-1 md:col-span-1 ${t.span} group bg-card border border-foreground/10 hover:border-green-600 transition-colors duration-300 rounded-lg shadow-md`} style={{
          transitionDelay: gridVisible ? `${i * 80}ms` : "0ms"
        }}>
              <Button onClick={() => setOpenIndex(i)} variant="ghost" className="relative w-full aspect-video overflow-hidden block h-auto p-0 rounded-t-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600" aria-label={`Play testimonial from ${t.name}`}>
                <img src={t.thumbnail} data-wvc-srcset={t.thumbnailSrcset} data-wvc-sizes={t.thumbnailSizes} alt={`${t.name} testimonial`} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]" />
                <div className="absolute inset-0 bg-green-900/20 group-hover:bg-green-900/30 transition-colors duration-300" />

                <span aria-hidden="true" className="absolute inset-0 flex items-center justify-center">
                  <span className="relative flex items-center justify-center">
                    <span className="absolute w-[60px] h-[60px] rounded-lg bg-blue-600/30 group-hover:scale-[2] opacity-0 group-hover:opacity-100 transition-all duration-500 ease-out" />
                    <span className="relative w-[60px] h-[60px] rounded-lg bg-blue-600 flex items-center justify-center transition-transform duration-300 ease-out group-hover:scale-110">
                      <Play className="w-6 h-6 text-white fill-white" strokeWidth={1.75} />
                    </span>
                  </span>
                </span>

                <span className="absolute bottom-3 left-3 font-mono text-[0.6875rem] uppercase tracking-[0.12em] text-white bg-green-800/80 px-2 py-1 rounded-md">
                  REC · 02:1{i}
                </span>
              </Button>

              <div className="p-5 md:p-6 grid grid-cols-[64px_1fr] gap-4 items-start">
                <div className="relative w-16 h-16 shrink-0">
                  <img src={t.headshot} data-wvc-srcset={t.headshotSrcset} data-wvc-sizes={t.headshotSizes} alt={t.name} loading="lazy" decoding="async" className="w-16 h-16 object-cover rounded-lg" />
                </div>

                <div className="min-w-0">
                  <h3 className="font-default font-semibold uppercase text-foreground" style={{
                fontSize: "clamp(1.0625rem, 1.3vw, 1.25rem)",
                letterSpacing: "-0.01em",
                lineHeight: "1.15"
              }}>
                    {t.name}
                  </h3>
                  <p className="font-mono text-[0.6875rem] uppercase tracking-[0.12em] text-green-700 mt-1">
                    {t.role} · {t.company}
                  </p>
                  <div className="mt-3 flex gap-2">
                    <Quote className="w-4 h-4 text-blue-600 shrink-0 mt-1" strokeWidth={1.75} />
                    <p className="font-default text-sm md:text-[0.9375rem] text-foreground/85" style={{
                  lineHeight: "1.55"
                }}>
                      {t.quote}
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-5 md:px-6 pb-5 md:pb-6 -mt-1 flex items-center justify-between">
                <span className="font-mono text-[0.6875rem] uppercase tracking-[0.12em] text-foreground/60">
                  FILE № {String(i + 1).padStart(2, "0")} / 2
                </span>
                <Button variant="link" onClick={() => setOpenIndex(i)} className="font-mono text-[0.75rem] uppercase tracking-[0.12em] text-blue-600 hover:text-blue-700 px-0 h-auto">
                  PLAY CLIP →
                </Button>
              </div>
            </article>)}
        </div>
      </div>

      <div aria-hidden="true" className="h-[14px] w-full" style={{
      backgroundImage: `url("${diamondPlateSvg}")`,
      backgroundRepeat: "repeat"
    }} />

      <Dialog open={openIndex !== null} onOpenChange={open => !open && setOpenIndex(null)}>
        <DialogContent showCloseButton={false} className="max-w-[1280px] w-[calc(100%-2rem)] bg-background border border-green-600 p-0 rounded-lg shadow-lg">
          {openIndex !== null && <div>
              <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-green-100">
                <div className="flex items-center gap-3">
                  <span aria-hidden="true" className="inline-block w-2 h-2 bg-blue-600 animate-pulse" />
                  <span className="font-mono text-[0.6875rem] md:text-xs uppercase tracking-[0.18em] text-foreground/80">
                    W.O. № 04 · FILE {String(openIndex + 1).padStart(2, "0")}
                  </span>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setOpenIndex(null)} className="text-foreground hover:bg-green-50 hover:text-blue-600" aria-label="Close">
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="relative aspect-video bg-green-50">
                <img src={testimonials[openIndex].thumbnail} data-wvc-srcset={testimonials[openIndex].thumbnailSrcset} data-wvc-sizes={testimonials[openIndex].thumbnailSizes} alt={testimonials[openIndex].name} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-40" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-[88px] h-[88px] rounded-lg bg-blue-600 flex items-center justify-center shadow-lg">
                    <Play className="w-9 h-9 text-white fill-white" strokeWidth={1.75} />
                  </div>
                </div>
              </div>

              <div className="px-4 md:px-6 py-4 border-t border-green-100 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 md:gap-6 items-start md:items-center">
                <div>
                  <DialogTitle className="font-default font-semibold uppercase text-foreground text-xl md:text-2xl">
                    {testimonials[openIndex].name}
                  </DialogTitle>
                  <p className="font-mono text-[0.6875rem] uppercase tracking-[0.12em] text-green-700 mt-1">
                    {testimonials[openIndex].role} ·{" "}
                    {testimonials[openIndex].company}
                  </p>
                </div>
                <p className="font-default text-sm md:text-base text-foreground/85 md:max-w-md">
                  <span className="text-blue-600 mr-1">"</span>
                  {testimonials[openIndex].quote}
                </p>
              </div>
            </div>}
        </DialogContent>
      </Dialog>
    </section>;
}